'use client';

import { c as _c } from "react/compiler-runtime";
import * as z from 'zod';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import type { TFunction } from 'i18next';
import { useSearchParams } from 'next/navigation';
import { Suspense, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { ButtonStyle } from '@sola/ark-core/atoms/button-style/button-style.tsx';
import { IntlBlock } from '@sola/ark-core/atoms/intl/Intl.tsx';
import { Alert } from '@sola/ark-core/molecules/alert/alert.tsx';
import { getVolunteerLocationPath, getVolunteerTourPath } from '@sola/contentful-model/lib/volunteer.ts';
import { absoluteUrlFromPath } from '@sola/skippy-sdk/urls.ts';
import type { VolunteerLocationItemDto } from '@sola/skippy-search-sdk/lib/schema.ts';
import { TrackVolunteerList } from '@sola/tracking/components/datalayer-volunteer.tsx';
import { trackVolunteerSelectContent } from '@sola/tracking/components/datalayer.tsx';
import { getBaseSizeProps } from '../../utils/text-utils.tsx';
import { buildLocationMedia, formatEventDateRange, formatLocationDisplay } from '../../utils/volunteer-utils.ts';
import { CardList } from '../card-list/card-list.tsx';
import { Card, LocationCardSkeleton } from '../card/card.tsx';
import { FeatureBase } from '../feature-base/feature-base.tsx';
import { useVolunteerList } from './volunteer-list.logic.ts';

// ─── Card Config ─────────────────────────────────────────────────────────────

interface VolunteerCardConfig {
  title: string;
  subtitle: string;
  body: React.ReactNode;
  badge?: {
    text: string;
    color: 'warning' | 'positive';
    size: 'sm';
  };
  media: unknown;
  primaryCTA?: {
    title: string;
    link: string;
  };
  secondaryCTA?: {
    title: string;
    link: string;
  };
  variant: 'event' | 'post';
}
const buildBadgeMap = (t: TFunction) => ({
  urgent: {
    text: t('volunteers:badge.urgent'),
    color: 'warning' as const,
    size: 'sm' as const
  },
  waitlist: {
    text: t('volunteers:badge.waitlist'),
    color: 'positive' as const,
    size: 'sm' as const
  }
});
const buildVolunteerCardConfig = (volunteer: VolunteerLocationItemDto, options: {
  variant: 'default' | 'tour';
  linkSuffix: string;
  t: TFunction;
}): VolunteerCardConfig => {
  const {
    variant,
    linkSuffix,
    t
  } = options;
  const isTour = (volunteer.tourCount ?? 0) > 1;
  const isTourList = variant === 'tour';
  const dateDisplay = formatEventDateRange(volunteer, isTour && !isTourList, isTourList ? 'card' : 'default');
  const locationDisplay = formatLocationDisplay(volunteer, isTour, isTourList, t('volunteers:locationTbd'));
  const badges = buildBadgeMap(t);
  if (isTourList) {
    return {
      title: locationDisplay,
      subtitle: dateDisplay,
      body: <p>
          {volunteer.venueName ?? ''}
          <br />
          {t('volunteers:slotsOpen', {
          count: volunteer.openSlots
        })}
        </p>,
      badge: badges[volunteer.status as keyof typeof badges],
      media: undefined,
      primaryCTA: volunteer.status === 'waitlist' ? undefined : {
        title: t('volunteers:seeDetails'),
        link: getVolunteerLocationPath(volunteer.slug, volunteer.eventId)
      },
      secondaryCTA:
      // TODO: temporarily disabled – waitlist join from tour grid
      // volunteer.status === 'waitlist'
      //   ? {
      //       title: t('volunteers:joinWaitlist'),
      //       link: `${getVolunteerLocationPath(volunteer.slug, volunteer.eventId)}${linkSuffix}`,
      //     }
      //   : undefined,
      undefined,
      variant: 'event'
    };
  }
  return {
    // Default cards are a collapsed tour group, so title by tour name.
    title: volunteer.tourName || volunteer.locationName,
    subtitle: dateDisplay,
    body: locationDisplay,
    badge: undefined,
    media: undefined,
    primaryCTA: {
      title: t('volunteers:seeDetails'),
      link: volunteer.tourCount === 1 ? getVolunteerLocationPath(volunteer.slug, volunteer.eventId) : `${getVolunteerTourPath(volunteer.slug)}${linkSuffix}`
    },
    variant: 'post'
  };
};

// ─── Component ───────────────────────────────────────────────────────────────

/**
 * Isolated QueryClient for CMS preview and standalone usage where the
 * VolunteerList is rendered outside the app-level QueryClientProvider.
 */
const useQueryClient = () => {
  const ref = useRef<QueryClient | null>(null);
  if (!ref.current) {
    ref.current = new QueryClient();
  }
  return ref.current;
};
export const VolunteerListProps = z.object({
  seoLevel: z.number().int().optional(),
  q: z.string().optional(),
  limit: z.number().int().optional(),
  onVolunteerLoad: z.function({
    input: [z.object({
      totalCount: z.number()
    })]
  }).optional(),
  variant: z.enum(['default', 'tour']).optional(),
  slug: z.string().optional(),
  excludeContentfulEntryID: z.string().optional(),
  /** When true, filters are read from URL params (dynamic). When false, uses static CMS props. */
  isDynamic: z.boolean().optional(),
  geoCoords: z.object({
    city: z.string().optional(),
    state: z.string().optional(),
    latitude: z.number().optional(),
    longitude: z.number().optional()
  }).optional(),
  isLocationResolving: z.boolean().optional()
});
export type VolunteerListProps = z.infer<typeof VolunteerListProps>;
const VolunteerListSkeleton: React.FC<{
  itemCount?: number;
  columns?: number;
  columnsMd?: number;
  columnsLg?: number;
}> = t0 => {
  const $ = _c(7);
  const {
    itemCount: t1,
    columns: t2,
    columnsMd: t3,
    columnsLg: t4
  } = t0;
  const itemCount = t1 === undefined ? 12 : t1;
  const columns = t2 === undefined ? 1 : t2;
  const columnsMd = t3 === undefined ? 3 : t3;
  const columnsLg = t4 === undefined ? 4 : t4;
  let t5;
  if ($[0] !== itemCount) {
    t5 = Array.from({
      length: itemCount
    }, _temp);
    $[0] = itemCount;
    $[1] = t5;
  } else {
    t5 = $[1];
  }
  let t6;
  if ($[2] !== columns || $[3] !== columnsLg || $[4] !== columnsMd || $[5] !== t5) {
    t6 = <CardList columns={columns} columnsMd={columnsMd} columnsLg={columnsLg} classNameGap="gap-y-roomy-xs gap-x-comfy-xl">{t5}</CardList>;
    $[2] = columns;
    $[3] = columnsLg;
    $[4] = columnsMd;
    $[5] = t5;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  return t6;
};
const InternalVolunteerList: React.FC<VolunteerListProps> = t0 => {
  const $ = _c(66);
  let onVolunteerLoad;
  let props;
  let slug;
  let t1;
  let t2;
  let t3;
  let t4;
  if ($[0] !== t0) {
    ({
      seoLevel: t1,
      onVolunteerLoad,
      isDynamic: t2,
      isLocationResolving: t3,
      variant: t4,
      slug,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = onVolunteerLoad;
    $[2] = props;
    $[3] = slug;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
    $[7] = t4;
  } else {
    onVolunteerLoad = $[1];
    props = $[2];
    slug = $[3];
    t1 = $[4];
    t2 = $[5];
    t3 = $[6];
    t4 = $[7];
  }
  const seoLevel = t1 === undefined ? 3 : t1;
  const isDynamic = t2 === undefined ? false : t2;
  const isLocationResolving = t3 === undefined ? false : t3;
  const variant = t4 === undefined ? "default" : t4;
  let t5;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = ["volunteers"];
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  const {
    t
  } = useTranslation(t5);
  const searchParams = useSearchParams();
  let t6;
  if ($[9] !== searchParams) {
    t6 = searchParams?.get("q") ?? null;
    $[9] = searchParams;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  const qParam = t6;
  const linkSuffix = qParam ? `?q=${encodeURIComponent(qParam)}` : "";
  let t7;
  if ($[11] !== isDynamic || $[12] !== isLocationResolving || $[13] !== onVolunteerLoad || $[14] !== props || $[15] !== slug || $[16] !== variant) {
    t7 = {
      ...props,
      isDynamic,
      onVolunteerLoad,
      variant,
      slug,
      isLocationResolving
    };
    $[11] = isDynamic;
    $[12] = isLocationResolving;
    $[13] = onVolunteerLoad;
    $[14] = props;
    $[15] = slug;
    $[16] = variant;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  const {
    locations,
    isLoading,
    isLoadingMore,
    isError,
    resolvedLimit,
    columnLayout,
    hasMore,
    handleLoadMore,
    tracking
  } = useVolunteerList(t7);
  let t8;
  if ($[18] !== tracking) {
    t8 = <TrackVolunteerList {...tracking} />;
    $[18] = tracking;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  const tracker = t8;
  if (isLocationResolving && variant !== "tour" || isLoading) {
    let t9;
    if ($[20] !== columnLayout || $[21] !== resolvedLimit) {
      t9 = <VolunteerListSkeleton itemCount={resolvedLimit} {...columnLayout} />;
      $[20] = columnLayout;
      $[21] = resolvedLimit;
      $[22] = t9;
    } else {
      t9 = $[22];
    }
    let t10;
    if ($[23] !== t9 || $[24] !== tracker) {
      t10 = <>{tracker}{t9}</>;
      $[23] = t9;
      $[24] = tracker;
      $[25] = t10;
    } else {
      t10 = $[25];
    }
    return t10;
  }
  if (isError) {
    let t9;
    if ($[26] !== t) {
      t9 = t("volunteers:errorLoading");
      $[26] = t;
      $[27] = t9;
    } else {
      t9 = $[27];
    }
    let t10;
    if ($[28] !== t9) {
      t10 = <Alert variant="negative" message={t9} />;
      $[28] = t9;
      $[29] = t10;
    } else {
      t10 = $[29];
    }
    let t11;
    if ($[30] !== t10 || $[31] !== tracker) {
      t11 = <div className="VolunteerList flex flex-col gap-comfy-sm">{tracker}{t10}</div>;
      $[30] = t10;
      $[31] = tracker;
      $[32] = t11;
    } else {
      t11 = $[32];
    }
    return t11;
  }
  if (!locations?.length) {
    let t9;
    if ($[33] !== t) {
      t9 = t("volunteers:noResultsTitle");
      $[33] = t;
      $[34] = t9;
    } else {
      t9 = $[34];
    }
    let t10;
    if ($[35] === Symbol.for("react.memo_cache_sentinel")) {
      t10 = <IntlBlock i18nKey="volunteers:noResultsBody" components={{
        1: <p />
      }} />;
      $[35] = t10;
    } else {
      t10 = $[35];
    }
    let t11;
    if ($[36] !== t9) {
      t11 = <FeatureBase title={t9} body={t10} />;
      $[36] = t9;
      $[37] = t11;
    } else {
      t11 = $[37];
    }
    let t12;
    if ($[38] !== t11 || $[39] !== tracker) {
      t12 = <div className="VolunteerList flex flex-col gap-comfy-sm max-w-md">{tracker}{t11}</div>;
      $[38] = t11;
      $[39] = tracker;
      $[40] = t12;
    } else {
      t12 = $[40];
    }
    return t12;
  }
  let t9;
  if ($[41] !== linkSuffix || $[42] !== locations || $[43] !== seoLevel || $[44] !== t || $[45] !== tracking || $[46] !== variant) {
    let t10;
    if ($[48] !== linkSuffix || $[49] !== seoLevel || $[50] !== t || $[51] !== tracking || $[52] !== variant) {
      t10 = volunteer => {
        const cardConfig = buildVolunteerCardConfig(volunteer, {
          variant,
          linkSuffix,
          t
        });
        return <Card key={volunteer.eventId} {...getBaseSizeProps(4)} seoLevel={seoLevel} title={cardConfig.title} subtitle={cardConfig.subtitle} body={cardConfig.body} badge={cardConfig.badge} media={cardConfig.variant === "post" ? buildLocationMedia(volunteer) : undefined} imageIntent="third" primaryCTA={cardConfig.primaryCTA} secondaryCTA={cardConfig.secondaryCTA} variant={cardConfig.variant} hasPlaceholder={true} alt={true} onClick={() => trackVolunteerSelectContent({
          location: volunteer,
          list: {
            slug: tracking.slug,
            tourName: tracking.tourName
          },
          isTour: (volunteer.tourCount ?? 0) > 1,
          contentUrl: cardConfig.primaryCTA?.link ? absoluteUrlFromPath(cardConfig.primaryCTA.link) : undefined
        })} />;
      };
      $[48] = linkSuffix;
      $[49] = seoLevel;
      $[50] = t;
      $[51] = tracking;
      $[52] = variant;
      $[53] = t10;
    } else {
      t10 = $[53];
    }
    t9 = locations.map(t10);
    $[41] = linkSuffix;
    $[42] = locations;
    $[43] = seoLevel;
    $[44] = t;
    $[45] = tracking;
    $[46] = variant;
    $[47] = t9;
  } else {
    t9 = $[47];
  }
  let t10;
  if ($[54] !== columnLayout || $[55] !== t9) {
    t10 = <CardList {...columnLayout} classNameGap="gap-y-roomy-xs gap-x-comfy-xl">{t9}</CardList>;
    $[54] = columnLayout;
    $[55] = t9;
    $[56] = t10;
  } else {
    t10 = $[56];
  }
  let t11;
  if ($[57] !== handleLoadMore || $[58] !== hasMore || $[59] !== isLoadingMore || $[60] !== t) {
    t11 = hasMore && <div className="flex justify-center mt-comfy-lg"><ButtonStyle variant="secondary" size="lg"><button type="button" onClick={handleLoadMore} disabled={isLoadingMore}>{isLoadingMore ? t("volunteers:loading", "Loading...") : t("volunteers:loadMore", "Load more")}</button></ButtonStyle></div>;
    $[57] = handleLoadMore;
    $[58] = hasMore;
    $[59] = isLoadingMore;
    $[60] = t;
    $[61] = t11;
  } else {
    t11 = $[61];
  }
  let t12;
  if ($[62] !== t10 || $[63] !== t11 || $[64] !== tracker) {
    t12 = <div className="VolunteerList flex flex-col gap-comfy-sm">{tracker}{t10}{t11}</div>;
    $[62] = t10;
    $[63] = t11;
    $[64] = tracker;
    $[65] = t12;
  } else {
    t12 = $[65];
  }
  return t12;
};
export const VolunteerList: React.FC<VolunteerListProps> = props => {
  const $ = _c(6);
  const queryClient = useQueryClient();
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <VolunteerListSkeleton />;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] !== props) {
    t1 = <InternalVolunteerList {...props} />;
    $[1] = props;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== queryClient || $[4] !== t1) {
    t2 = <Suspense fallback={t0}><QueryClientProvider client={queryClient}>{t1}</QueryClientProvider></Suspense>;
    $[3] = queryClient;
    $[4] = t1;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  return t2;
};
function _temp(_, index) {
  return <LocationCardSkeleton key={`loading-volunteer-skeleton-${index}`} />;
}